import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress, IconButton,
} from "@mui/material";
import { CheckCircleOutline, ArrowBack } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

function Field({ label, required, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase" }}>
          {label}{required && <Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

export default function ForgotPassword() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);

  const bg      = isDark ? "#080d1a" : "#f4f7ff";
  const cardBg  = isDark ? "#111827" : "#ffffff";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,35,96,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,53,122,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,53,122,0.08)" },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/forgot-password`, { email: email.trim().toLowerCase() });
    } catch {
      // Always show sent to prevent account enumeration
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  const cardWrapper = (children) => (
    <Box sx={{
      width: { xs: "100%", sm: 460 }, maxWidth: 460, borderRadius: 3, overflow: "hidden",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
      boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
      bgcolor: cardBg,
    }}>
      <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
      {children}
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative" }}>

      {/* Background blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%",  size: 500, color: isDark ? "rgba(0,53,122,0.18)" : "rgba(0,53,122,0.07)", delay: 0 },
          { top: "60%",  right: "-8%", size: 400, color: isDark ? "rgba(192,168,140,0.08)" : "rgba(192,168,140,0.12)", delay: 1.5 },
        ].map((b, i) => (
          <motion.div key={i} style={{ position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%", background: b.color, filter: "blur(80px)" }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: 2, delay: b.delay, ease: "easeInOut" }} />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -64, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }} style={{ position: "relative", zIndex: 10 }}>
        <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.97)",
          backdropFilter: "blur(16px)", borderBottom: "2px solid rgba(192,168,140,0.3)" }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 64,
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton component={Link} to="/"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                <ArrowBack sx={{ fontSize: 20 }} />
              </IconButton>
              <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
              </Box>
            </Box>
            <ThemeToggle />
          </Box>
        </Box>
      </motion.div>

      {/* Main */}
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, py: 6, position: "relative", zIndex: 10 }}>
        <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.15, ease: [0.34, 1.56, 0.64, 1] }}>

          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div key="sent"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}>
                {cardWrapper(
                  <>
                    <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center",
                      position: "relative", overflow: "hidden" }}>
                      <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                        borderRadius: "50%", border: "1px solid rgba(192,168,140,0.15)" }} />
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}>
                        <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
                      </motion.div>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
                        RESET LINK SENT
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
                        Check Your Inbox
                      </Typography>
                    </Box>
                    <Box sx={{ px: 4, py: 3.5 }}>
                      <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155",
                        lineHeight: 1.8, mb: 2.5, textAlign: "center" }}>
                        If an account exists for <strong>{email}</strong>, a password reset link
                        has been sent. The link expires in <strong>1 hour</strong> — use it quickly.
                      </Typography>

                      {/* What to do box */}
                      <Box sx={{
                        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#f8f6f2",
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,122,0.08)"}`,
                        borderRadius: "10px", px: 2.5, py: 2, mb: 2.5,
                      }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? "#94a3b8" : "#64748b",
                          letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.25 }}>
                          What to do
                        </Typography>
                        {[
                          "Open your inbox and look for an email from Labarium",
                          "Click the secure reset link in the email",
                          "Create your new password on the page that opens",
                          "Sign in with your new password",
                        ].map((step, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: i < 3 ? 1.25 : 0 }}>
                            <Box sx={{
                              width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                              bgcolor: BRAND.navy, color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: 12.5, fontWeight: 800, mt: 0.1,
                            }}>
                              {i + 1}
                            </Box>
                            <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569", lineHeight: 1.65 }}>
                              {step}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Typography sx={{ fontSize: 14, color: isDark ? "#64748b" : "#64748b",
                        textAlign: "center", mb: 3, lineHeight: 1.7 }}>
                        Don't see it? Check your spam or junk folder. Still nothing?{" "}
                        <Box component="a" href="mailto:info@labarium.com"
                          sx={{ color: isDark ? BRAND.gold : BRAND.navy, fontWeight: 700, textDecoration: "underline" }}>
                          Contact us
                        </Box>
                      </Typography>
                      <Button fullWidth variant="outlined" component={Link} to="/"
                        sx={{
                          borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,53,122,0.2)",
                          color: isDark ? "#94a3b8" : "#64748b",
                          borderRadius: "10px", py: 1.3, fontWeight: 600, textTransform: "none", fontSize: 14,
                          "&:hover": { borderColor: BRAND.navy, color: BRAND.navy, bgcolor: "transparent" },
                        }}>
                        Back to Sign In
                      </Button>
                    </Box>
                  </>
                )}
              </motion.div>
            ) : (
              <motion.div key="form" exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }}>
                {cardWrapper(
                  <>
                    <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3, pb: 3.5, position: "relative", overflow: "hidden" }}>
                      <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140,
                        borderRadius: "50%", border: "1px solid rgba(192,168,140,0.15)" }} />
                      <Box sx={{ position: "absolute", top: -20, right: -20, width: 90, height: 90,
                        borderRadius: "50%", border: "1px solid rgba(192,168,140,0.2)" }} />
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.gold, mb: 1 }}>
                        ACCOUNT RECOVERY
                      </Typography>
                      <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                        Forgot Password?
                      </Typography>
                      <Typography sx={{ fontSize: 15.5, color: "rgba(255,255,255,0.6)", mt: 0.75, lineHeight: 1.55 }}>
                        Enter your registered email and we'll send you a secure password reset link instantly.
                      </Typography>
                    </Box>
                    <Box sx={{ px: 4, py: 4 }} component="form" onSubmit={handleSubmit} noValidate>
                      <Field label="Email address" required isDark={isDark}>
                        <TextField hiddenLabel type="email" fullWidth required
                          placeholder="you@labarium.com"
                          value={email} onChange={(e) => setEmail(e.target.value)}
                          sx={inputSx} />
                      </Field>

                      <Button type="submit" fullWidth variant="contained" disabled={loading}
                        sx={{
                          mt: 0.5, mb: 2,
                          background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
                          borderRadius: "10px", py: 1.6, fontWeight: 700, textTransform: "none", fontSize: 17,
                          boxShadow: "0 4px 14px rgba(0,53,122,0.35)",
                          "&:hover": { boxShadow: "0 6px 20px rgba(0,53,122,0.45)", bgcolor: BRAND.navyDark },
                        }}>
                        {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Send Reset Link"}
                      </Button>

                      <Typography sx={{ textAlign: "center", fontSize: 15, color: textSub }}>
                        <Box component={Link} to="/"
                          sx={{ color: BRAND.gold, textDecoration: "underline", fontWeight: 600 }}>
                          Back to Sign In
                        </Box>
                      </Typography>
                    </Box>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <Typography sx={{ mt: 2, fontSize: 14.5, color: isDark ? "#334155" : "#9ca3af", textAlign: "center" }}>
            AI analysis does not constitute medical advice. Always consult a licensed provider.
          </Typography>
        </motion.div>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: isDark ? "rgba(2,6,15,0.9)" : BRAND.navyDark, py: 2, textAlign: "center",
        position: "relative", zIndex: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.25)", letterSpacing: "0.05em" }}>
          © {new Date().getFullYear()} LABARIUM DIAGNOSTIC SYSTEMS, INC. ALL RIGHTS RESERVED.
        </Typography>
      </Box>
    </Box>
  );
}
